import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Carousel } from '@/components/organisms/Carousel';
import { LearningCard } from '../../molecules/LearningCard';
import styles from './StoryCarousel.module.scss';
import type { StoryCarouselItem, StoryCarouselProps } from './StoryCarousel.types';
import type { LearningCardStyle } from '../../molecules/LearningCard/LearningCard.types';

export function StoryCarousel({
  title,
  subtitle,
  stories,
  className,
}: StoryCarouselProps) {
  const { t } = useTranslation();

  const renderCard = (story: StoryCarouselItem, index: number, style: LearningCardStyle) => (
    <LearningCard
      key={index}
      use="story"
      style={style}
      title={story.title}
      description={story.description}
      tag={story.category ?? t('common.general')}
      xpReward={story.xpReward}
      estimatedTime={story.estimatedTime}
      completed={story.completed}
      backgroundImage={story.backgroundImage}
      onClick={story.onClick}
      showButton={story.showButton}
      buttonText={story.buttonText}
      buttonVariant={story.buttonVariant}
      onButtonClick={story.onButtonClick}
      buttonDisabled={story.buttonDisabled}
    />
  );

  // Con una sola historia el carrusel no aporta (y suele dar problemas de layout):
  // se muestra la card a ANCHO COMPLETO (variante 'wide'), igual que las cards que
  // van fuera de un carrusel. Con 2+ historias, carrusel normal.
  const singleStory = stories.length === 1 ? stories[0] : undefined;
  if (singleStory) {
    return (
      <div className={cn(styles.single, className)}>
        {(title || subtitle) && (
          <div className={styles.single__header}>
            {title && <h2 className={styles.single__title}>{title}</h2>}
            {subtitle && <p className={styles.single__subtitle}>{subtitle}</p>}
          </div>
        )}
        {renderCard(singleStory, 0, 'wide')}
      </div>
    );
  }

  return (
    <Carousel title={title} subtitle={subtitle} className={className}>
      {stories.map((story, index) => renderCard(story, index, story.variant ?? 'normal'))}
    </Carousel>
  );
}

StoryCarousel.displayName = 'StoryCarousel';
